import React, { useState } from 'react';
import {
  Box,
  Typography,
  InputAdornment,
  TextField,
  IconButton,
  Tooltip } from
'@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SettingsIcon from '@mui/icons-material/Settings';
import EnhancedDataTable from './EnhancedDataTable';

/**
 * SearchableDataTable - A wrapper component that combines search functionality
 * with the EnhancedDataTable to provide a consistent search experience across all pages.
 * 
 * This component solves the focus issues that occur when search inputs are embedded
 * within DataGrid toolbars by placing the search input outside the DataGrid.
 * 
 * The search input is completely isolated from DataGrid's focus management system,
 * ensuring reliable typing behavior without interference.
 */
const SearchableDataTable = ({
  // Search-related props
  title = '',
  searchPlaceholder = 'Search...',

  // All EnhancedDataTable props - pass through everything
  rows,
  columns,
  loading = false,
  tableName = 'default',
  onRowClick = null,
  height = 600,
  checkboxSelection = false,
  disableSelectionOnClick = true,
  onSelectionModelChange = null,
  selectionModel = [],
  hideFooter = true,
  disableColumnMenu = false,
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
  disableToolbar = false,
  additionalToolbarContent = null,

  // Pass through any other props
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState('');

  // Handle search text change
  const handleSearchChange = (event) => {

    setSearchText(event.target.value);
  };

  // Clear search text
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Handlers for column visibility and saved views - pass through to EnhancedDataTable
  const handleColumnVisibilityClick = (event) => {
    // This will be handled by the EnhancedDataTable via ref
    if (enhancedDataTableRef.current) {
      enhancedDataTableRef.current.handleColumnVisibilityClick(event);
    }
  };

  const handleViewMenuOpen = (event) => {
    // This will be handled by the EnhancedDataTable via ref
    if (enhancedDataTableRef.current) {
      enhancedDataTableRef.current.handleViewMenuOpen(event);
    }
  };

  // Ref to access EnhancedDataTable methods
  const enhancedDataTableRef = React.useRef();

  // Filter rows based on search text
  const filteredRows = React.useMemo(() => {
    if (!searchText) {
      return rows;
    }

    const searchLower = searchText.toLowerCase();
    return rows.filter((row) => {
      return Object.keys(row).some((key) => {
        const value = row[key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [rows, searchText]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search Header */}
      <Box sx={{
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {title &&
          <Typography variant="h6" component="div" sx={{ minWidth: 'fit-content' }}>
              {title}
            </Typography>
          }
          
          <TextField
            variant="outlined"
            size="small"
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              minWidth: '300px',
              maxWidth: '500px',
              flex: 1
            }}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>,

              endAdornment: searchText &&
              <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>

            }} />

        </Box>

        {/* Column Visibility and Saved Views Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Column Visibility">
            <IconButton onClick={handleColumnVisibilityClick}>
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Saved Views">
            <IconButton onClick={handleViewMenuOpen}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Enhanced Data Table */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <EnhancedDataTable
          ref={enhancedDataTableRef}
          rows={filteredRows}
          columns={columns}
          loading={loading}
          tableName={tableName}
          onRowClick={onRowClick}
          height={height}
          checkboxSelection={checkboxSelection}
          disableSelectionOnClick={disableSelectionOnClick}
          onSelectionModelChange={onSelectionModelChange}
          selectionModel={selectionModel}
          hideFooter={hideFooter}
          disableColumnMenu={disableColumnMenu}
          disableColumnFilter={disableColumnFilter}
          disableColumnSelector={disableColumnSelector}
          disableDensitySelector={disableDensitySelector}
          disableToolbar={true}
          additionalToolbarContent={additionalToolbarContent}
          // Pass through any additional props
          {...otherProps} />

      </Box>
    </Box>);

};

export default SearchableDataTable;
